import { useCallback, useReducer } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useCloudContext } from '../contexts/CloudProvider.jsx'

// Constants for better tree-shaking and type safety
const FORM_MODES = Object.freeze({
  LOGIN: 'login',
  REGISTER: 'register',
  FORGOT_PASSWORD: 'forgot_password',
  RESET_SUCCESS: 'reset_success',
  REGISTER_SUCCESS: 'register_success',
})

// State management with useReducer for better performance
const initialFormState = {
  mode: FORM_MODES.LOGIN,
  email: '',
  password: '',
  confirmPassword: '',
  displayName: '',
  loading: false,
  error: null,
}

const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.payload, error: null }
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false }
    case 'CLEAR_FORM':
      return { ...initialFormState, mode: state.mode, email: state.email }
    case 'RESET_PASSWORD_FIELDS':
      return { ...state, password: '', confirmPassword: '', error: null }
    default:
      return state
  }
}

/**
 * Login Page component for Firebase authentication
 * B2B-focused design with Google auth primary, email/password secondary
 * Optimized for React 19+ with minimal re-renders and modern patterns
 */
function LoginPage() {
  const [state, dispatch] = useReducer(formReducer, initialFormState)
  const { login, register, loginWithGoogle, resetPassword } = useCloudContext()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  // Get return URL from search params
  const returnUrl = searchParams.get('returnUrl') || '/'

  // Memoized handlers to prevent unnecessary re-renders
  const handleEmailSubmit = useCallback(async (e) => {
    e.preventDefault()
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })

    try {
      if (state.mode === FORM_MODES.LOGIN) {
        await login(state.email, state.password)
        navigate(returnUrl || '/dashboard', { replace: true })
      } else {
        // Validation
        if (state.password !== state.confirmPassword) {
          throw new Error('Passwords do not match')
        }
        if (!state.displayName?.trim()) {
          throw new Error('Please enter your full name')
        }
        if (state.displayName.trim().length < 2) {
          throw new Error('Name must be at least 2 characters long')
        }
        
        await register(state.email, state.password, state.displayName.trim())
        dispatch({ type: 'SET_MODE', payload: FORM_MODES.REGISTER_SUCCESS })
      }
    } catch (error) {
      const errorMessages = {
        'auth/user-not-found': 'No account found with this email. Please create an account or check your email.',
        'auth/wrong-password': 'Incorrect password. Please try again.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/email-already-in-use': 'An account with this email already exists. Please sign in instead.',
        'auth/weak-password': 'Password should be at least 6 characters long.',
        'auth/network-request-failed': 'Network error. Please check your connection and try again.',
      }
      
      dispatch({ 
        type: 'SET_ERROR', 
        payload: errorMessages[error.code] || error.message || 'Authentication failed. Please try again.',
      })
    }
  }, [state.mode, state.email, state.password, state.confirmPassword, state.displayName, login, register, navigate, returnUrl])

  const handleForgotPassword = useCallback(async (e) => {
    e.preventDefault()
    if (!state.email?.trim()) {
      dispatch({ type: 'SET_ERROR', payload: 'Please enter your email address first.' })
      return
    }
    
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    
    try {
      await resetPassword(state.email)
      dispatch({ type: 'SET_MODE', payload: FORM_MODES.RESET_SUCCESS })
    } catch (error) {
      const errorMessages = {
        'auth/user-not-found': 'No account found with this email address.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/too-many-requests': 'Too many password reset attempts. Please try again later.',
      }
      
      dispatch({ 
        type: 'SET_ERROR', 
        payload: errorMessages[error.code] || 'Failed to send password reset email. Please try again.',
      })
    }
  }, [state.email, resetPassword])
  
  const handleGoogleSignIn = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    
    try {
      await loginWithGoogle()
      navigate(returnUrl || '/dashboard', { replace: true })
    } catch (error) {
      const errorMessages = {
        'auth/popup-closed-by-user': 'Google sign-in was cancelled. Please try again.',
        'auth/popup-blocked': 'Pop-up was blocked. Please allow pop-ups and try again.',
        'auth/network-request-failed': 'Network error. Please check your connection and try again.',
      }
      
      dispatch({ 
        type: 'SET_ERROR', 
        payload: errorMessages[error.code] || error.message || 'Failed to sign in with Google. Please try again.',
      })
    }
  }, [loginWithGoogle, navigate, returnUrl])

  const handleFieldChange = useCallback((field, value) => {
    dispatch({ type: 'SET_FIELD', field, value })
  }, [])

  const handleModeChange = useCallback((mode) => {
    dispatch({ type: 'SET_MODE', payload: mode })
    dispatch({ type: 'RESET_PASSWORD_FIELDS' })
  }, [])

  // Reusable FormInput component for DRY principles
  const FormInput = useCallback(({ 
    id, 
    type = 'text', 
    label, 
    value, 
    field,
    placeholder, 
    required = false, 
    minLength,
    maxLength,
    autoComplete,
    autoFocus = false,
  }) => (
    <div className="mb-2">
      <label htmlFor={id} className="form-label" style={{ 
        fontSize: '0.75rem', 
        marginBottom: '0.25rem', 
        color: '#374151', 
        fontWeight: '600',
        letterSpacing: '0.01em',
      }}>
        {label}
      </label>
      <input
        type={type}
        className="form-control"
        id={id}
        value={value}
        onChange={(e) => handleFieldChange(field, e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={state.loading}
        minLength={minLength}
        maxLength={maxLength}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        style={{ 
          padding: '10px 12px', 
          fontSize: '0.85rem',
          borderRadius: '8px',
          border: '1px solid #d1d5db',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: '#fafbfc',
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#4285f4'
          e.target.style.backgroundColor = '#ffffff'
          e.target.style.boxShadow = '0 0 0 3px rgba(66, 133, 244, 0.1)'
        }}
        onBlur={(e) => {
          e.target.style.borderColor = '#d1d5db'
          e.target.style.backgroundColor = '#fafbfc'
          e.target.style.boxShadow = 'none'
        }}
      />
    </div>
  ), [handleFieldChange, state.loading])

  // Derived values for better performance
  const isLoginMode = state.mode === FORM_MODES.LOGIN
  const isRegisterMode = state.mode === FORM_MODES.REGISTER
  const isForgotPasswordMode = state.mode === FORM_MODES.FORGOT_PASSWORD
  const isResetSuccessMode = state.mode === FORM_MODES.RESET_SUCCESS
  const isRegisterSuccessMode = state.mode === FORM_MODES.REGISTER_SUCCESS
  return (
    <div 
      className="min-vh-100 d-flex align-items-center justify-content-center position-relative"
      style={{
        background: `
          linear-gradient(135deg, #0f1419 0%, #1a1d29 25%, #252938 50%, #1a1d29 75%, #0f1419 100%),
          radial-gradient(circle at 20% 50%, rgba(255, 0, 0, 0.03) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 0, 0, 0.02) 0%, transparent 50%),
          radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.01) 0%, transparent 50%)
        `,
        minHeight: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* Subtle animated background elements */}
      <div 
        className="position-absolute w-100 h-100"
        style={{
          background: `
            linear-gradient(90deg, transparent 0%, rgba(255, 0, 0, 0.01) 50%, transparent 100%),
            linear-gradient(0deg, transparent 0%, rgba(255, 255, 255, 0.005) 50%, transparent 100%)
          `,
          opacity: 0.3,
          animation: 'subtle-glow 8s ease-in-out infinite alternate',
        }}
      />
      
      {/* Grid pattern overlay for tech aesthetic */}
      <div 
        className="position-absolute w-100 h-100"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.01) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.01) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          opacity: 0.2,
        }}
      />

      <div className="container position-relative" style={{ zIndex: 'var(--z-content)' }}>
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-4">
            <div 
              className="card shadow-lg border-0"
              style={{
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                boxShadow: `
                  0 20px 60px rgba(0, 0, 0, 0.35),
                  0 8px 25px rgba(0, 0, 0, 0.15),
                  0 0 0 1px rgba(255, 255, 255, 0.08),
                  inset 0 1px 0 rgba(255, 255, 255, 0.15)
                `,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div className="card-body p-4">
                {/* Logo and Title */}
                <div className="text-center mb-2">
                  <div className="mb-1 d-flex justify-content-center align-items-center">
                    <img 
                      src="/images/Revatek-logo.svg" 
                      alt="Revatek Logo" 
                      style={{ 
                        height: '36px', 
                        width: 'auto',
                        maxWidth: '160px',
                        objectFit: 'contain',
                        display: 'block',
                        filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                      }}
                      className="img-fluid"
                    />
                  </div>
                  <h1 className="h5 mb-1 fw-semibold login-brand-text-gradient" id="signin-heading">
                    Revatek Connect
                  </h1>
                  <p className="text-muted mb-0" style={{ 
                    fontSize: '0.8rem',
                    color: '#6c757d',
                    fontWeight: '400',
                    letterSpacing: '0.01em',
                  }}>
                    {isLoginMode ? 'Sign in to your account' : 'Create your account'}
                  </p>
                </div>

                {/* Error Alert */}
                {state.error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {state.error}
                  </div>
                )}

                {/* Registration Success Alert */}
                {isRegisterSuccessMode && (
                  <div className="alert alert-success" role="alert">
                    <i className="bi bi-check-circle me-2"></i>
                    Account created successfully! Please check your email for a verification link.
                  </div>
                )}                {/* PRIMARY: Google Sign-In Button (Enhanced Google Brand Standards) */}
                <div className="mb-2">
                  <button
                    type="button"
                    className="btn w-100 d-flex align-items-center justify-content-center position-relative"
                    style={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #dadce0',
                      color: '#3c4043',
                      fontWeight: '500',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      padding: '10px 16px',
                      fontSize: '0.85rem',
                      borderRadius: '10px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => {
                      if (!state.loading) {
                        e.target.style.boxShadow = '0 2px 8px rgba(66, 133, 244, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)'
                        e.target.style.transform = 'translateY(-1px)'
                        e.target.style.backgroundColor = '#fafbff'
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
                      e.target.style.transform = 'translateY(0)'
                      e.target.style.backgroundColor = '#ffffff'
                    }}
                    onMouseDown={(e) => {
                      if (!state.loading) {
                        e.target.style.transform = 'translateY(0)'
                      }
                    }}
                    onClick={handleGoogleSignIn}
                    disabled={state.loading}
                    aria-label="Sign in with your Google account"
                  >
                    {state.loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing in...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-google me-2" style={{ color: '#4285f4' }}></i>
                        Sign in with Google
                      </>
                    )}
                  </button>
                </div>

                {/* Compact Divider */}
                {!isForgotPasswordMode && (
                  <div className="text-center mb-2 position-relative" style={{ margin: '1rem 0' }}>
                    <div 
                      style={{ 
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.08) 50%, transparent 100%)',
                        position: 'relative',
                      }} 
                    />
                    <span 
                      className="position-absolute bg-white px-2"
                      style={{ 
                        top: '50%', 
                        left: '50%', 
                        transform: 'translate(-50%, -50%)',
                        fontSize: '0.7rem',
                        color: '#9ca3af',
                        fontWeight: '500',
                        letterSpacing: '0.025em',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      or email
                    </span>
                  </div>
                )}                {/* SECONDARY: Email/Password Form (Enhanced B2B Design) */}
                {!isForgotPasswordMode && (
                  <form onSubmit={handleEmailSubmit} role="form" aria-labelledby="signin-heading">
                    <FormInput
                      id="email"
                      type="email"
                      label="Email"
                      value={state.email}
                      field="email"
                      placeholder="your@email.com"
                      required
                      autoComplete="email"
                    />
                    
                    {/* Display Name for Registration */}
                    {isRegisterMode && (
                      <FormInput
                        id="displayName"
                        type="text"
                        label="Full Name"
                        value={state.displayName}
                        field="displayName"
                        placeholder="John Doe"
                        required
                        minLength={2}
                        maxLength={50}
                        autoComplete="name"
                      />
                    )}
                    
                    <div className="mb-2">
                      <label htmlFor="password" className="form-label" style={{ 
                        fontSize: '0.75rem', 
                        marginBottom: '0.25rem', 
                        color: '#374151', 
                        fontWeight: '600',
                        letterSpacing: '0.01em',
                      }}>Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="password"
                        value={state.password}
                        onChange={(e) => handleFieldChange('password', e.target.value)}
                        required
                        disabled={state.loading}
                        minLength={6}
                        autoComplete={isLoginMode ? 'current-password' : 'new-password'}
                        style={{ 
                          padding: '10px 12px', 
                          fontSize: '0.85rem',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          backgroundColor: '#fafbfc',
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#4285f4'
                          e.target.style.backgroundColor = '#ffffff'
                          e.target.style.boxShadow = '0 0 0 3px rgba(66, 133, 244, 0.1)'
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#d1d5db'
                          e.target.style.backgroundColor = '#fafbfc'
                          e.target.style.boxShadow = 'none'
                        }}
                      />
                      {isLoginMode && (
                        <div className="form-text text-end" style={{ marginTop: '0.25rem' }}>
                          <a 
                            href="#" 
                            className="text-decoration-none" 
                            style={{ 
                              color: '#ff0000', 
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              transition: 'color 0.2s ease',
                            }}
                            onClick={(e) => {
                              e.preventDefault()
                              handleModeChange(FORM_MODES.FORGOT_PASSWORD)
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.color = '#dc2626'
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.color = '#ff0000'
                            }}
                          >
                            Forgot password?
                          </a>
                        </div>
                      )}
                    </div>
                    
                    {/* Confirm Password for Registration */}
                    {isRegisterMode && (
                      <FormInput
                        id="confirmPassword"
                        type="password"
                        label="Confirm Password"
                        value={state.confirmPassword}
                        field="confirmPassword"
                        required
                        minLength={6}
                        autoComplete="new-password"
                      />
                    )}

                    {/* Enhanced Submit Button */}
                    <button
                      type="submit"
                      className="btn w-100 mb-2 position-relative overflow-hidden"
                      style={{
                        background: 'linear-gradient(135deg, #ff0000 0%, #dc2626 100%)',
                        border: 'none',
                        color: 'white',
                        fontWeight: '600',
                        padding: '10px 16px',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        fontSize: '0.85rem',
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(255, 0, 0, 0.3)',
                        letterSpacing: '0.025em',
                      }}
                      disabled={state.loading}
                      onMouseEnter={(e) => {
                        if (!state.loading) {
                          e.target.style.transform = 'translateY(-1px)'
                          e.target.style.boxShadow = '0 6px 20px rgba(255, 0, 0, 0.4)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)'
                        e.target.style.boxShadow = '0 4px 12px rgba(255, 0, 0, 0.3)'
                      }}
                      onMouseDown={(e) => {
                        if (!state.loading) {
                          e.target.style.transform = 'translateY(0)'
                        }
                      }}
                    >
                      {state.loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          {isLoginMode ? 'Signing In...' : 'Creating Account...'}
                        </>
                      ) : (
                        <>
                          {isLoginMode ? 'Sign In' : 'Create Account'}
                        </>
                      )}
                    </button>

                    {/* Enhanced Toggle between Login/Register */}
                    <div className="text-center mb-2">
                      <button
                        type="button"
                        className="btn btn-link text-decoration-none p-0"
                        style={{ 
                          fontSize: '0.8rem',
                          color: '#6b7280',
                          fontWeight: '500',
                          transition: 'color 0.2s ease',
                          letterSpacing: '0.01em',
                        }}
                        onClick={() => {
                          handleModeChange(isLoginMode ? FORM_MODES.REGISTER : FORM_MODES.LOGIN)
                        }}
                        disabled={state.loading}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#374151'
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#6b7280'
                        }}
                      >
                        {isLoginMode 
                          ? "Don't have an account? Create one" 
                          : 'Already have an account? Sign in'
                        }
                      </button>
                    </div>
                  </form>
                )}

                {/* FORGOT PASSWORD FORM */}
                {isForgotPasswordMode && (
                  <div className="forgot-password-form">
                    {isResetSuccessMode ? (
                      <div className="text-center">
                        <div className="alert alert-success" role="alert">
                          <i className="bi bi-check-circle me-2"></i>
                          Password reset email sent!
                        </div>
                        <p className="text-muted mb-3">
                          Check your email for instructions to reset your password. 
                          The email might take a few minutes to arrive.
                        </p>
                        <div className="d-grid gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                              handleModeChange(FORM_MODES.LOGIN)
                            }}
                          >
                            ← Back to Sign In
                          </button>
                          <button
                            type="button"
                            className="btn revatek-btn"
                            style={{
                              backgroundColor: '#ff0000',
                              borderColor: '#ff0000',
                              color: 'white',
                            }}
                            onClick={() => window.open('https://gmail.com', '_blank')}
                          >
                            Open Email
                          </button>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleForgotPassword} role="form" aria-label="Reset password">
                        <div className="text-center mb-3">
                          <h5 style={{ color: '#ff0000' }}>Reset Your Password</h5>
                          <p className="text-muted">
                            Enter your email address and we&apos;ll send you a link to reset your password.
                          </p>
                        </div>

                        <FormInput
                          id="resetEmail"
                          type="email"
                          label="Email"
                          value={state.email}
                          field="email"
                          placeholder="your@email.com"
                          required
                          autoComplete="email"
                          autoFocus
                        />

                        <div className="d-grid gap-2 mb-3">
                          <button
                            type="submit"
                            className="btn revatek-btn"
                            style={{
                              backgroundColor: '#ff0000',
                              borderColor: '#ff0000',
                              color: 'white',
                              fontWeight: '500',
                              padding: '12px 16px',
                            }}
                            disabled={state.loading || !state.email?.trim()}
                          >
                            {state.loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Sending Reset Email...
                              </>
                            ) : (
                              'Send Reset Email'
                            )}
                          </button>
                          
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                              handleModeChange(FORM_MODES.LOGIN)
                            }}
                            disabled={state.loading}
                          >
                            ← Back to Sign In
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {/* Help Links */}
                <div className="text-center mt-2">
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Need help? <a 
                      href="https://revatek.com/pages/contact"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none"
                      style={{ 
                        color: '#ff0000', 
                        fontSize: 'inherit',
                        transition: 'color 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#dc2626'
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#ff0000'
                      }}
                    >
                      Contact Support
                    </a>
                  </small>
                </div>
              </div>        </div>
      </div>

      {/* World-class dark theme styling with animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes subtle-glow {
            0% { opacity: 0.2; transform: translateY(0px); }
            100% { opacity: 0.4; transform: translateY(-5px); }
          }
          
          @keyframes card-entrance {
            0% { 
              opacity: 0; 
              transform: translateY(20px) scale(0.95); 
            }
            100% { 
              opacity: 1; 
              transform: translateY(0px) scale(1); 
            }
          }
          
          .card {
            animation: card-entrance 0.6s ease-out;
            border-radius: 20px !important;
            border: none !important;
            background: rgba(255, 255, 255, 0.98) !important;
            backdrop-filter: blur(20px) !important;
            box-shadow: 
              0 20px 60px rgba(0, 0, 0, 0.35),
              0 8px 25px rgba(0, 0, 0, 0.15),
              0 0 0 1px rgba(255, 255, 255, 0.08),
              inset 0 1px 0 rgba(255, 255, 255, 0.15) !important;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          
          .card:hover {
            transform: translateY(-2px) !important;
            box-shadow: 
              0 25px 70px rgba(0, 0, 0, 0.4),
              0 10px 30px rgba(0, 0, 0, 0.2),
              0 0 0 1px rgba(255, 255, 255, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
          }
          
          .card-body {
            padding: 1.5rem !important;
          }
          
          .form-control, .form-control-sm {
            background: rgba(250, 251, 252, 0.9) !important;
            border: 1px solid #d1d5db !important;
            border-radius: 8px !important;
            padding: 10px 12px !important;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
            font-size: 0.85rem !important;
          }
          
          .form-control:hover:not(:focus):not(:disabled) {
            border-color: #9ca3af !important;
            background: rgba(255, 255, 255, 0.95) !important;
            transform: translateY(-1px) !important;
          }
          
          .form-control:focus {
            border-color: #4285f4 !important;
            box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1) !important;
            background: rgba(255, 255, 255, 1) !important;
            transform: translateY(0) !important;
          }
          
          .btn:focus {
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(255, 0, 0, 0.25) !important;
          }
          
          .revatek-btn {
            border-radius: 5px !important;
            padding: 8px 16px !important;
            font-weight: 500 !important;
            transition: all 0.2s ease !important;
            background: linear-gradient(135deg, #ff0000 0%, #e60000 100%) !important;
            border: none !important;
            box-shadow: 0 2px 4px rgba(255, 0, 0, 0.2) !important;
            font-size: 0.85rem !important;
          }
          
          .revatek-btn:hover:not(:disabled) {
            background: linear-gradient(135deg, #e60000 0%, #cc0000 100%) !important;
            transform: translateY(-1px) !important;
            box-shadow: 
              0 4px 12px rgba(255, 0, 0, 0.3),
              0 0 0 0.15rem rgba(255, 0, 0, 0.1) !important;
          }
          
          .revatek-btn:active:not(:disabled) {
            transform: translateY(0px) !important;
            box-shadow: 0 2px 6px rgba(255, 0, 0, 0.2) !important;
          }
          
          .btn-outline-secondary {
            border-radius: 8px !important;
            padding: 12px 16px !important;
            border: 1px solid rgba(0, 0, 0, 0.1) !important;
            background: rgba(255, 255, 255, 0.8) !important;
            transition: all 0.2s ease !important;
          }
          
          .btn-outline-secondary:hover {
            background: rgba(248, 249, 250, 0.9) !important;
            border-color: rgba(0, 0, 0, 0.2) !important;
            transform: translateY(-1px) !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
          }
          
          /* Google button enhancements for dark theme */
          .btn[style*="backgroundColor: #ffffff"] {
            border-radius: 5px !important;
            padding: 8px 16px !important;
            transition: all 0.2s ease !important;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
            font-size: 0.85rem !important;
          }
          
          .btn[style*="backgroundColor: #ffffff"]:hover:not(:disabled) {
            transform: translateY(-1px) !important;
            box-shadow: 
              0 4px 12px rgba(0, 0, 0, 0.15),
              0 1px 2px 0 rgba(60, 64, 67, 0.30), 
              0 1px 3px 1px rgba(60, 64, 67, 0.15) !important;
          }
          
          /* Alert styling for dark theme */
          .alert {
            border-radius: 8px !important;
            border: none !important;
            background: rgba(220, 53, 69, 0.1) !important;
            color: #dc3545 !important;
            border-left: 4px solid #dc3545 !important;
          }
          
          /* Logo glow effect */
          .img-fluid {
            filter: drop-shadow(0 2px 8px rgba(255, 0, 0, 0.1));
            transition: filter 0.3s ease;
          }
          
          .img-fluid:hover {
            filter: drop-shadow(0 4px 12px rgba(255, 0, 0, 0.2));
          }
          
          /* Text enhancements */
          h1, h3, h5 {
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          }
          
          /* Link styling */
          a, .btn-link {
            color: #ff0000 !important;
            transition: all 0.2s ease !important;
          }
          
          a:hover, .btn-link:hover {
            color: #e60000 !important;
            text-shadow: 0 1px 2px rgba(255, 0, 0, 0.2) !important;
          }
          
          /* Loading spinner enhancement */
          .spinner-border {
            filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
          }
          
          /* Responsive enhancements */
          @media (max-width: 768px) {
            .card-body {
              padding: 1rem !important;
            }
            
            .card {
              margin: 0.5rem !important;
              border-radius: 12px !important;
            }
          }
          
          @media (max-height: 800px) {
            .card-body {
              padding: 1rem !important;
            }
            
            .img-fluid {
              height: 35px !important;
            }
            
            h1, .h5 {
              font-size: 1rem !important;
              margin-bottom: 0.25rem !important;
            }
            
            p.small {
              font-size: 0.7rem !important;
              margin-bottom: 0 !important;
            }
          }
          
          @media (max-height: 700px) {
            .card-body {
              padding: 0.75rem !important;
            }
            
            .img-fluid {
              height: 30px !important;
            }
          }
        `,
      }} />
    </div>
      </div>

      {/* Enhanced focus states for better accessibility */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .form-control:focus {
            border-color: #4285f4 !important;
            box-shadow: 0 0 0 0.2rem rgba(66, 133, 244, 0.25) !important;
          }
          
          .btn:focus {
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(66, 133, 244, 0.25) !important;
          }
          
          .btn-primary:focus {
            border-color: #4285f4 !important;
          }        `,
      }} />
    </div>
  )
}

export default LoginPage
